import { useCallback, useRef, useState, type KeyboardEvent } from 'react'

import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import useVoiceRecording from '@/hooks/useVoiceRecording'
import { Loader2, Mic, SendHorizontal, Square } from 'lucide-react'

interface ChatInputProps {
  onSend: (message: string) => void
  onStop?: () => void
  isStreaming: boolean
  placeholder?: string
}

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return `${m}:${s.toString().padStart(2, '0')}`
}

export default function ChatInput({
  onSend,
  onStop,
  isStreaming,
  placeholder = 'Ask a question...',
}: ChatInputProps) {
  const [value, setValue] = useState('')
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const { isRecording, isTranscribing, duration, error, startRecording, stopRecording } =
    useVoiceRecording({
      onTranscript: (text) => setValue(text),
    })

  const handleSend = useCallback(() => {
    const trimmed = value.trim()
    if (!trimmed || isStreaming) return
    onSend(trimmed)
    setValue('')
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'
    }
  }, [value, isStreaming, onSend])

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        handleSend()
      }
    },
    [handleSend],
  )

  const handleInput = useCallback(() => {
    const el = textareaRef.current
    if (el) {
      el.style.height = 'auto'
      el.style.height = `${Math.min(el.scrollHeight, 200)}px`
    }
  }, [])

  const renderActionButton = () => {
    if (isStreaming) {
      return (
        <Button variant="outline" size="icon" onClick={onStop} className="shrink-0">
          <Square className="h-4 w-4" />
        </Button>
      )
    }

    if (isRecording) {
      return (
        <Button variant="outline" size="icon" onClick={stopRecording} className="shrink-0">
          <Square className="h-4 w-4 text-red-500" />
        </Button>
      )
    }

    if (isTranscribing) {
      return (
        <Button variant="outline" size="icon" disabled className="shrink-0">
          <Loader2 className="h-4 w-4 animate-spin" />
        </Button>
      )
    }

    if (value.trim()) {
      return (
        <Button size="icon" onClick={handleSend} className="shrink-0">
          <SendHorizontal className="h-4 w-4" />
        </Button>
      )
    }

    return (
      <Button variant="outline" size="icon" onClick={startRecording} className="shrink-0">
        <Mic className="h-4 w-4" />
      </Button>
    )
  }

  return (
    <div className="shrink-0 border-t bg-background px-4 py-2">
      <div className="mx-auto flex max-w-3xl items-end gap-2">
        {isRecording ? (
          <div className="flex min-h-11 flex-1 items-center gap-3 rounded-md border px-3 py-2">
            <span className="h-2.5 w-2.5 animate-pulse rounded-full bg-red-500" />
            <span className="text-sm text-muted-foreground">
              Recording {formatDuration(duration)}
            </span>
          </div>
        ) : (
          <Textarea
            ref={textareaRef}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={handleKeyDown}
            onInput={handleInput}
            placeholder={placeholder}
            disabled={isStreaming || isTranscribing}
            rows={1}
            className="max-h-[200px] min-h-[44px] resize-none"
          />
        )}
        {renderActionButton()}
      </div>
      {error && <p className="mx-auto mt-1 max-w-3xl text-xs text-destructive">{error}</p>}
    </div>
  )
}
